import { useEffect, useRef } from "react";

import { getPollingInterval, shouldUpdateVersion } from "@/utils/versionCheck";

/**
 * Hook that periodically checks for new versions from the server.
 * Shows notification automatically when a new version is detected.
 *
 * @param onVersionUpdate - Callback function called when a new version is detected
 */
const IS_E2E = import.meta.env.MODE === "test";

export const usePeriodicVersionCheck = (
  onVersionUpdate?: (hasUpdate: boolean) => void,
): void => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(onVersionUpdate);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = onVersionUpdate;
  }, [onVersionUpdate]);

  useEffect(() => {
    if (IS_E2E) {
      return;
    }

    const checkForUpdate = () => {
      try {
        const hasUpdate = shouldUpdateVersion();

        if (hasUpdate) {
          // New version detected - trigger callback if provided
          if (callbackRef.current) {
            callbackRef.current(true);
          }
        }
      } catch (error) {
        // Silently fail - don't spam console on network errors
        // Version check failures are not critical
        console.warn("Version check failed:", error);
      }
    };

    // Check immediately on mount (respects rate limiting)
    checkForUpdate();

    // Then check periodically
    const interval = getPollingInterval();
    intervalRef.current = setInterval(checkForUpdate, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); // Only run once on mount
};
